/**
 * NearU Card Component
 * ====================
 * Premium virtual card display with luxury aesthetic.
 */

import React from 'react';
import { Snowflake, CreditCard } from 'lucide-react';

const NearUCard = ({ 
  firstName = 'Member',
  lastFour = '••••',
  expiry = '••/••',
  isActive = false,
  isFrozen = false,
  balance = 0,
  compact = false
}) => {
  
  const formatBalance = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  };

  if (compact) {
    return (
      <div 
        data-testid="nearu-card-compact"
        style={{
          background: 'linear-gradient(145deg, #1A0F0A 0%, #2C1B13 50%, #1A0F0A 100%)',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(215, 181, 109, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Gold accent line */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #D7B56D, transparent)'
        }} />
        
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CreditCard size={24} color="#2C1B13" />
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ 
            color: '#D7B56D', 
            fontSize: '14px', 
            fontWeight: '600',
            fontFamily: 'Cinzel, serif'
          }}>
            NearU Card
          </div>
          <div style={{ color: '#8A7355', fontSize: '12px', marginTop: '2px' }}>
            •••• {lastFour}
          </div>
        </div>
        
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#E6D7C3', fontSize: '16px', fontWeight: '700' }}>
            {formatBalance(balance)}
          </div>
          <div style={{ color: '#4CAF50', fontSize: '11px' }}>Available</div>
        </div>

        {isFrozen && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(33, 150, 243, 0.2)',
            padding: '4px 8px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Snowflake size={12} color="#2196F3" />
            <span style={{ fontSize: '10px', color: '#2196F3', fontWeight: '600' }}>FROZEN</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      data-testid="nearu-card"
      style={{
        width: '100%',
        maxWidth: '360px',
        aspectRatio: '1.586',
        borderRadius: '16px',
        background: 'linear-gradient(145deg, #1A0F0A 0%, #2C1B13 30%, #1A0F0A 70%, #2C1B13 100%)',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `
          0 4px 20px rgba(0, 0, 0, 0.5),
          0 0 40px rgba(215, 181, 109, 0.1),
          inset 0 1px 0 rgba(215, 181, 109, 0.2)
        `,
        border: '1px solid rgba(215, 181, 109, 0.2)'
      }}
    >
      {/* Subtle pattern overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(215, 181, 109, 0.03) 0%, transparent 50%),
                          radial-gradient(circle at 80% 20%, rgba(215, 181, 109, 0.03) 0%, transparent 50%)`,
        pointerEvents: 'none'
      }} />

      {/* NearU Logo */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(215, 181, 109, 0.4)'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" 
                  fill="#2C1B13"/>
            <circle cx="12" cy="9" r="2.5" fill="#D7B56D"/>
          </svg>
        </div>
        <span style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '16px',
          fontWeight: '600',
          color: '#D7B56D',
          letterSpacing: '2px'
        }}>
          NEARU
        </span>
        
        {isFrozen && (
          <div style={{
            marginLeft: 'auto',
            background: 'rgba(33, 150, 243, 0.2)',
            padding: '4px 10px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Snowflake size={14} color="#2196F3" />
            <span style={{ fontSize: '11px', color: '#2196F3', fontWeight: '600' }}>FROZEN</span>
          </div>
        )}
      </div>

      {/* Chip */}
      <div style={{
        width: '45px',
        height: '35px',
        borderRadius: '6px',
        background: 'linear-gradient(145deg, #C9A962 0%, #9A7B3A 50%, #C9A962 100%)',
        marginBottom: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '30px',
          height: '20px',
          borderRadius: '3px',
          border: '1px solid rgba(0,0,0,0.2)',
          background: 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
        }}>
          {/* Chip lines */}
          <div style={{ position: 'absolute', top: '5px', left: 0, right: 0, height: '1px', background: 'rgba(0,0,0,0.2)' }} />
          <div style={{ position: 'absolute', top: '10px', left: 0, right: 0, height: '1px', background: 'rgba(0,0,0,0.2)' }} />
          <div style={{ position: 'absolute', top: '15px', left: 0, right: 0, height: '1px', background: 'rgba(0,0,0,0.2)' }} />
        </div>
      </div>

      {/* Card Number */}
      <div style={{
        fontFamily: 'SF Mono, Consolas, monospace',
        fontSize: '18px',
        letterSpacing: '3px',
        color: '#E6D7C3',
        marginBottom: '20px'
      }}>
        •••• •••• •••• {lastFour}
      </div>

      {/* Bottom Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end'
      }}>
        <div>
          <div style={{
            fontSize: '9px',
            color: '#8A7355',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '4px'
          }}>
            Card Holder
          </div>
          <div style={{
            fontSize: '14px',
            color: '#E6D7C3',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            {firstName}
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: '9px',
            color: '#8A7355',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '4px'
          }}>
            Valid Thru
          </div>
          <div style={{
            fontSize: '14px',
            color: '#E6D7C3',
            fontWeight: '500',
            letterSpacing: '1px'
          }}>
            {expiry}
          </div>
        </div>
      </div>

      {/* Balance overlay for active card */}
      {isActive && balance > 0 && (
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: 'rgba(76, 175, 80, 0.15)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '10px',
          padding: '8px 12px',
          textAlign: 'right'
        }}>
          <div style={{ fontSize: '10px', color: '#4CAF50', marginBottom: '2px' }}>Available</div>
          <div style={{ fontSize: '16px', color: '#4CAF50', fontWeight: '700' }}>
            {formatBalance(balance)}
          </div>
        </div>
      )}
    </div>
  );
};

export default NearUCard;
